/**
 * Tiny typed fetch client.
 *
 * - Always sends cookies (session)
 * - Adds CSRF header on mutations when a token is known
 * - Throws a typed `ApiError` on non-2xx responses
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClientOptions {
  baseUrl?: string;
  csrfToken?: string | null;
}

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  csrfToken?: string | null;
  signal?: AbortSignal;
}

/** Invoked whenever the server rejects a request with 403 FORBIDDEN. */
export type ForbiddenHandler = () => void;

export class ApiClient {
  private readonly baseUrl: string;
  private csrfToken: string | null;
  private forbiddenHandler: ForbiddenHandler | null = null;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? '';
    this.csrfToken = options.csrfToken ?? null;
  }

  setCsrfToken(token: string | null): void {
    this.csrfToken = token;
  }
  getCsrfToken(): string | null {
    return this.csrfToken;
  }

  /**
   * Registered once by the auth store. A 403 means the server rejected the
   * request based on the account's *current* role, which may differ from
   * the role cached client-side (e.g. an admin just changed it in another
   * tab/session). Used to trigger a resync so permission-gated UI stops
   * acting on stale data.
   */
  setForbiddenHandler(handler: ForbiddenHandler | null): void {
    this.forbiddenHandler = handler;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const method = options.method ?? 'GET';
    const headers = new Headers();
    const init: RequestInit = { method, credentials: 'include', headers };
    if (options.body !== undefined) {
      headers.set('content-type', 'application/json');
      init.body = JSON.stringify(options.body);
    }
    if (options.signal) init.signal = options.signal;
    const csrfToken = options.csrfToken ?? this.csrfToken;
    if (method !== 'GET' && csrfToken) headers.set('x-csrf-token', csrfToken);

    const response = await fetch(`${this.baseUrl}${path}`, init);
    if (response.status === 204) return undefined as T;

    const text = await response.text();
    const data: unknown = text.length > 0 ? (JSON.parse(text) as unknown) : null;
    if (!response.ok) {
      if (response.status === 403) this.forbiddenHandler?.();
      const errBody =
        (data as { error?: { code?: string; message?: string; details?: unknown } } | null) ?? null;
      throw new ApiError(
        response.status,
        errBody?.error?.code ?? 'UNKNOWN',
        errBody?.error?.message ?? response.statusText,
        errBody?.error?.details,
      );
    }
    return data as T;
  }

  get<T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }
  post<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }
  patch<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }
  delete<T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }
}

export const api = new ApiClient({ baseUrl: '' });
